import { Stack, styled, Typography } from "@mui/material";

import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "@/components/custom/data-table";
import { SettlementColumns } from "./_components/settlement-columns";
import CustomPagination from "@/components/custom/custom-pagination";
import { useMemo } from "react";
import { useSettlements } from "@/hooks/use-settlements";
import { useSettlementStats } from "@/hooks/use-settlement-stats";
import { SettlementsSummary } from "../data";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import { formatCurrency } from "@/utils/format-currency";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const Settlements = () => {
  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";

  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const status = searchParams.get("status");

  const queryParams = {
    page: currentPage,
    size: pageSize,
    status: status ? status : undefined,
    search: searchValue,
  };

  const {
    data: settlements,
    isLoading,
    isError,
  } = useSettlements(true, queryParams);

  const columns = useMemo(() => SettlementColumns, []);

  const { stats, isLoading: isStatsLoading } = useSettlementStats();

  return (
    <SectionRoot>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {SettlementsSummary.map(({ title, objKeyRepr, icon, isCurrency }, idx) => (
            <CustomSimpleCard
              key={`${objKeyRepr}-${idx}`}
              title={title}
              icon={icon}
              isLoading={isStatsLoading}
            >
              <Typography variant="h6">
                {isCurrency
                  ? formatCurrency(stats?.[objKeyRepr] ?? 0)
                  : (stats?.[objKeyRepr] ?? 0)}
              </Typography>
            </CustomSimpleCard>
          ))}
        </Stack>

        <Card>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <CustomSearchInput
              paramKey="search"
              placeholder="Search settlements..."
            />
            <CustomSelectInput
              paramKey="status"
              label="Status"
              placeholder="All Status"
              options={[
                { label: "Awaiting 24Hrs", value: "AWAITING24HRS" },
                { label: "Initiated", value: "INITIATED" },
                { label: "Pending", value: "PENDING" },
                { label: "Settled", value: "SETTLED" },
                { label: "Failed", value: "FAILED" },
                { label: "Refunded", value: "REFUNDED" },
              ]}
            />
          </Stack>

          <DataTable
            data={settlements?.data?.data ?? []}
            columns={columns}
            isLoading={isLoading}
            isError={isError}
            noDataMessage="No settlements found."
          />

          <CustomPagination
            total={settlements?.data?.totalCount ?? 0}
            perPageOptions={[5, 10, 20, 50, 100]}
          />
        </Card>
      </Stack>
    </SectionRoot>
  );
};
